import React, { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from "recharts";

const DmeDashboard = () => {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedChain, setSelectedChain] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [userChains, setUserChains] = useState([]);
    const { get } = useApi();
    const { accounts } = useMsal();
    const user = accounts[0];
    const userEmail = user?.username;

    const COLORS = {
        0: "#10b981",  // Verde - Por Clasificar
        1: "#ef4444",  // Rojo - Error
        3: "#a855f7",  // Púrpura - Retenido
        4: "#f97316",  // Naranja - Cancelado
        5: "#6b7280",  // Gris - Duplicado
        6: "#fbbf24",  // Amarillo oscuro - En Aclaración
        10: "#3b82f6", // Azul - Clasificado
    };

    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        setFromDate(today);
        setToDate(today);
    }, []);

    // cadenas permitidas
    useEffect(() => {
        const fetchChains = async () => {
            if (!userEmail) return;
            try {
                const res = await get("chainsByUserEmail", { urlParams: { id: userEmail } });
                const chains = (res || []).map((c) => c.chain).filter(Boolean);
                setUserChains(chains);
            } catch {
                setUserChains([]);
            }
        };
        fetchChains();
    }, [userEmail, get]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            // Si no hay cadena seleccionada, usar todas las permitidas del usuario
            const chainParam = selectedChain || userChains.join(",");

            const response = await get("DashboardToday", {
                params: {
                    chain: chainParam,
                    from: fromDate,
                    to: toDate,
                },
            });
            setDashboard(response);
        } catch (err) {
            console.error("Error loading dashboard:", err);
            toast.error("Error cargando el dashboard");
            setDashboard(null);
        } finally {
            setLoading(false);
        }
    };

    // Datos para gráfica de conteos
    const chartData = dashboard?.statusCounts?.map((item) => ({
        name: item.statusText,
        value: item.count,
        status: item.status,
    })) || [];

    // Datos para gráfica de montos
    const chartAmountData = dashboard?.statusCounts?.map((item) => ({
        name: item.statusText,
        value: item.amount,
        status: item.status,
    })) || [];

    console.log(chartAmountData);

    // Calcular total de folios desde statusCounts
    const totalFolios = dashboard?.statusCounts?.reduce((sum, item) => sum + item.count, 0) || 0;
    const totalAmount = dashboard?.totalAmount || 0;

    // Definir todos los estatus posibles
    const allStatuses = [
        { status: 0, name: "Por Clasificar" },
        { status: 1, name: "Error" },
        { status: 3, name: "Retenido" },
        { status: 4, name: "Cancelado" },
        { status: 5, name: "Duplicado" },
        { status: 6, name: "En Aclaración" },
        { status: 10, name: "Clasificado" },
    ];

    // Crear un mapa de datos existentes
    const statusCountMap = {};
    const statusAmountMap = {};
    dashboard?.statusCounts?.forEach((item) => {
        statusCountMap[item.status] = item.count;
        statusAmountMap[item.status] = item.amount;
    });

    // Combinar todos los estatus con sus conteos
    const allStatusesWithCounts = allStatuses
        .map((status) => ({
            status: status.status,
            name: status.name,
            count: statusCountMap[status.status] || 0,
            amount: statusAmountMap[status.status] || 0,
        }))
        .sort((a, b) => {
            if (a.count > 0 && b.count === 0) return -1;
            if (a.count === 0 && b.count > 0) return 1;
            return b.count - a.count;
        });

    // Datos para la gráfica de barras de progreso de folios
    const folioProgressData = [
        {
            name: "Por Clasificar",
            value: statusCountMap[0] || 0,
            color: COLORS[0],
        },
        {
            name: "Enviados a QmovilE",
            value: dashboard?.foliosSentToQmovil || 0,
            color: "#0ea5e9",
        },
        {
            name: "Clasificados",
            value: statusCountMap[10] || 0,
            color: COLORS[10],
        },
    ];

    if (loading) {
        return <div className="text-center py-10 text-gray-500">Cargando dashboard...</div>;
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">Dashboard DME</h1>

                <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Desde</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="border rounded px-3 py-2 text-sm w-full"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Hasta</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="border rounded px-3 py-2 text-sm w-full"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Cadena</label>
                        <select
                            value={selectedChain}
                            onChange={(e) => setSelectedChain(e.target.value)}
                            className="border rounded px-3 py-2 text-sm w-full"
                        >
                            <option value="">Todas las cadenas</option>
                            {userChains.map((chain) => (
                                <option key={chain} value={chain}>
                                    {chain}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={loadDashboard}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm w-full"
                        >
                            Buscar
                        </button>
                    </div>
                </div>

                {/* Resumen en tres columnas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Card 1: Archivos cargados */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
                        <div className="text-sm font-medium opacity-90 mb-2">Archivos Cargados</div>
                        <div className="text-4xl font-bold">{dashboard?.totalFolios || 0}</div>
                    </div>

                    {/* Card 2: Total de folios */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
                        <div className="text-sm font-medium opacity-90 mb-2">Total de Folios</div>
                        <div className="text-4xl font-bold">
                            {dashboard?.statusCounts?.reduce((sum, item) => sum + item.count, 0) || 0}
                        </div>
                    </div>

                    {/* Card 3: Monto total */}
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-lg">
                        <div className="text-sm font-medium opacity-90 mb-2">Monto Total</div>
                        <div className="text-4xl font-bold">
                            ${totalAmount.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Gráficos */}
            {dashboard && chartData.length > 0 ? (
                <div className="space-y-6">
                    {/* SECCIÓN 1: Distribución de Folios */}
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Distribución de Folios</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Gráfico de Pastel - Folios */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Por Estatus
                                </h2>
                                <ResponsiveContainer width="100%" height={450}>
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={false}
                                            outerRadius={120}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[entry.status] || "#8884d8"}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => `${value} folios`} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Glosario de Folios */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Detalle por Estatus
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                                    {allStatusesWithCounts.map((item, index) => (
                                        <div
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg transition ${item.count > 0
                                                ? "bg-gray-50 hover:bg-gray-100"
                                                : "bg-gray-100 opacity-50"
                                                }`}
                                        >
                                            <div
                                                className="w-5 h-5 rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: COLORS[item.status],
                                                }}
                                            ></div>
                                            <div className="flex-grow">
                                                <p className="text-sm font-medium text-gray-700">
                                                    {item.name}
                                                </p>
                                                <p className="text-lg font-bold text-gray-900">
                                                    {item.count} folios
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-gray-500">
                                                    {totalFolios > 0
                                                        ? (
                                                            (item.count /
                                                                totalFolios) *
                                                            100
                                                        ).toFixed(1)
                                                        : 0}
                                                    %
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: Distribución de Montos */}
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Distribución de Montos</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Gráfico de Pastel - Montos */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Por Estatus
                                </h2>
                                <ResponsiveContainer width="100%" height={450}>
                                    <PieChart>
                                        <Pie
                                            data={chartAmountData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={false}
                                            outerRadius={120}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {chartAmountData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[entry.status] || "#8884d8"}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => `$${value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Glosario de Montos */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Detalle por Estatus
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                                    {allStatusesWithCounts.map((item, index) => (
                                        <div
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg transition ${item.amount > 0
                                                ? "bg-gray-50 hover:bg-gray-100"
                                                : "bg-gray-100 opacity-50"
                                                }`}
                                        >
                                            <div
                                                className="w-5 h-5 rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: COLORS[item.status],
                                                }}
                                            ></div>
                                            <div className="flex-grow">
                                                <p className="text-sm font-medium text-gray-700">
                                                    {item.name}
                                                </p>
                                                <p className="text-lg font-bold text-gray-900">
                                                    ${item.amount.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-gray-500">
                                                    {totalAmount > 0
                                                        ? (
                                                            (item.amount /
                                                                totalAmount) *
                                                            100
                                                        ).toFixed(1)
                                                        : 0}
                                                    %
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Progreso de Folios</h3>
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                Por Clasificar / Enviados a QmovilE / Clasificados
                            </h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={folioProgressData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 13, fontWeight: 600, fill: "#374151" }}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 12, fill: "#6b7280" }}
                                    />
                                    <Tooltip formatter={(value) => [`${value} folios`, "Cantidad"]} />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={80}>
                                        {folioProgressData.map((entry, index) => (
                                            <Cell key={`bar-cell-${index}`} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            style={{ fontSize: 14, fontWeight: "bold", fill: "#374151" }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                    No hay datos disponibles para el rango seleccionado
                </div>
            )}
        </div>
    );
};

export default DmeDashboard;